import { useEffect, useRef, useState } from 'react';
import { resolveCopyableChatTranscriptEntry, writeTextToClipboard } from '../timeline/chatMessageCopyUtils';
import { assignExistingChatMessages } from '../../../../api/chatTopics';
import useHistoryDismiss from '../../../../hooks/common/useHistoryDismiss';

export default function useChatTopicSelection(topics: any) {
  const [state, setState] = useState<any>({ modeKey: '', active: false, rows: [], picker: false, menu: false, busy: false, error: '' });
  const pending = useRef<any>(null);
  const liveMode = useRef(topics.modeKey); liveMode.current = topics.modeKey;
  const current = state.modeKey === topics.modeKey ? state : { active: false, rows: [], picker: false, menu: false, busy: false, error: '' };
  const ref = useRef<any>(null);
  const eligible = (message: any) => topics.role === 'admin' && topics.enabled && !topics.draftTopic && message?.id
    && !message.outboxItem && !message.outboxStatus && message.sender_type !== 'system'
    && ['text', 'image', 'file'].includes(message.message_format) && !message.question && !message.deleted_at
    && String(message.topic_id || message.topic?.id || '') === String(topics.topicId || '');
  const update = (change: any) => setState((old: any) => ({ ...(old.modeKey === topics.modeKey ? old : current), ...change, modeKey: topics.modeKey }));
  const start = (message: any) => { if (eligible(message) && !current.busy) update({ active: true, rows: [message], menu: false, error: '', notice: '' }); };
  const toggle = (message: any, onlyAdd = false) => {
    if (!eligible(message) || current.busy) return;
    const has = current.rows.some((row: any) => row.id === message.id);
    if (!has && current.rows.length >= 50) { update({ error: '一度に選択できるのは50件までです。' }); return; }
    update({ rows: has ? onlyAdd ? current.rows : current.rows.filter((row: any) => row.id !== message.id) : [...current.rows, message], error: '', notice: '' });
  };
  const finish = () => { if (!current.busy) { pending.current = null; update({ active: false, rows: [], picker: false, menu: false, error: '', notice: '' }); } };
  const openMenu = (rows = current.rows) => {
    if (!current.active || current.busy || !rows.length) return;
    update({ menu: true, rows, error: '', notice: '' });
    if (rows.length === 1) rows[0].openMessageMenu?.();
  };
  const longPress = (message: any) => {
    if (!current.active || !eligible(message) || current.busy) return;
    const has = current.rows.some((row: any) => row.id === message.id);
    if (!has && current.rows.length >= 50) { update({ error: '一度に選択できるのは50件までです。' }); return; }
    openMenu(has ? current.rows : [...current.rows, message]);
  };
  const closeMenu = () => { if (!current.busy) update({ menu: false }); };
  const consumeBack = () => {
    if (!current.active) return false;
    if (current.busy) return true;
    if (current.menu) closeMenu();
    else if (current.picker) update({ picker: false });
    else finish();
    return true;
  };
  const copy = async () => {
    if (current.busy || !current.rows.length) return;
    const modeKey = topics.modeKey;
    const ordered = [...current.rows].sort((a: any, b: any) => {
      const first = Date.parse(a.created_at), second = Date.parse(b.created_at);
      return Number.isFinite(first) && Number.isFinite(second) ? first - second || String(a.id).localeCompare(String(b.id)) : 0;
    });
    const text = ordered.map(resolveCopyableChatTranscriptEntry).filter(Boolean).join('\n\n');
    update({ busy: true, error: '', notice: '' });
    try {
      await writeTextToClipboard(text);
      if (liveMode.current === modeKey) update({ busy: false, notice: 'コピーしました' });
    } catch (error: any) {
      if (liveMode.current === modeKey) update({ busy: false, error: error.message || 'コピーできませんでした。' });
    }
  };
  const execute = async (destination?: any) => {
    if (current.busy || !current.rows.length) return;
    const modeKey = topics.modeKey;
    const body: any = { actorType: 'admin', scope: topics.scope, conversationId: topics.conversationId,
      membershipRevision: topics.loadedMembershipRevision || 0,
      messages: current.rows.map((row: any) => ({ id: row.id, revision: row.topic_membership_revision || 0 })),
      action: topics.topicId ? 'detach' : 'attach',
      ...(topics.topicId ? { sourceTopicId: topics.topicId } : destination?.id ? { topicId: destination.id } : { newTopic: destination }),
    };
    // A retry of the same draft must keep its creation ID even if the form remounts.
    if (body.newTopic && pending.current?.draft?.kind === body.newTopic.kind
      && pending.current.draft.name === body.newTopic.name && pending.current.draft.eventId === body.newTopic.eventId) {
      body.newTopic = { ...body.newTopic, clientId: pending.current.draft.clientId };
    }
    const signature = JSON.stringify(body);
    if (pending.current?.signature !== signature) pending.current = { signature, operationId: crypto.randomUUID(), draft: body.newTopic };
    update({ busy: true, error: '' });
    try {
      await assignExistingChatMessages({ ...body, operationId: pending.current.operationId });
      if (liveMode.current !== modeKey) return;
      pending.current = null;
      update({ busy: false, active: false, rows: [], picker: false, menu: false });
      await topics.refresh();
      topics.invalidateMessages?.();
    } catch (error: any) {
      if (liveMode.current === modeKey) update({ busy: false, error: error.message });
      throw error;
    }
  };
  const selection = { ...current, eligible, start, toggle, longPress, finish, execute, copy, openMenu, closeMenu, consumeBack,
    openPicker: () => { if (!current.busy && current.rows.length) update({ menu: false, picker: true, notice: '' }); },
    closePicker: () => { if (!current.busy) update({ picker: false }); },
    has: (message: any) => current.rows.some((row: any) => row.id === message?.id) };
  ref.current = selection;
  useHistoryDismiss(current.active, () => ref.current?.consumeBack());
  useEffect(() => {
    topics.selectionActive = current.active;
    return () => { topics.selectionActive = false; };
  }, [topics, current.active]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !ref.current?.active || ref.current.picker || event.defaultPrevented) return;
      if (document.querySelector('[role=menu], [role=dialog]')) return;
      event.preventDefault(); ref.current.consumeBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return selection;
}
